import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEstimate } from "../store/EstimateContext";
import { fmtHours, fmtCurrency } from "../lib/calculations";
import { FolderOpen, Trash2, RefreshCw, Copy, Share2, History, X, Search } from "lucide-react";
import { toast } from "sonner";

import HelpIconButton from "./HelpIconButton";
function HistoryModal({ projectId, onClose }) {
  const { listRevisions, restoreRevision } = useEstimate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const list = await listRevisions(projectId);
      setItems(list);
    } finally {
      setLoading(false);
    }
  };

  // initial load
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRestore = async (rev) => {
    if (!window.confirm(`Restore revision #${rev.revisionNumber} from ${new Date(rev.savedAt).toLocaleString()}?\n\nThe current state will be saved as a new revision first.`)) {
      return;
    }
    try {
      await restoreRevision(projectId, rev.revisionNumber);
      toast.success(`Restored revision #${rev.revisionNumber}`);
      onClose();
    } catch {
      toast.error("Failed to restore revision");
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4"
      onClick={onClose}
      data-testid="history-modal"
    >
      <div
        className="bg-white border border-gray-300 shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-gray-300 p-5">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
              Estimate Revisions
            </p>
            <h3 className="font-heading text-xl tracking-tight font-bold text-black">
              Version History
            </h3>
            <p className="text-xs text-gray-500 mt-1 font-mono">{projectId}</p>
          </div>
          <button
            onClick={onClose}
            data-testid="history-close-btn"
            className="text-gray-500 hover:text-black p-1"
            aria-label="Close history"
          >
            <X size={18} strokeWidth={2} />
          </button>
        </div>
        <div className="overflow-y-auto" data-testid="history-list">
          {loading ? (
            <div className="p-10 text-center text-gray-400 text-sm font-mono">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-gray-400 text-sm">
              No revisions yet. Saving an estimate after edits creates a new revision.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-100 text-black font-bold uppercase tracking-wider text-[10px] border-b border-gray-300 sticky top-0">
                  <th className="py-2 px-3 text-left">Revision</th>
                  <th className="py-2 px-3 text-left">Saved</th>
                  <th className="py-2 px-3 text-left">Project Name</th>
                  <th className="py-2 px-3 text-right">Hours</th>
                  <th className="py-2 px-3 text-right">Grand Total</th>
                  <th className="py-2 px-3"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((rev) => (
                  <tr key={rev.revisionNumber} className="border-b border-gray-200 hover:bg-[#F9FAFB]" data-testid={`history-row-${rev.revisionNumber}`}>
                    <td className="py-2 px-3 font-mono">#{rev.revisionNumber}</td>
                    <td className="py-2 px-3 font-mono text-xs">
                      {rev.savedAt ? new Date(rev.savedAt).toLocaleString() : "—"}
                    </td>
                    <td className="py-2 px-3">{rev.projectName || "—"}</td>
                    <td className="py-2 px-3 font-mono text-right">{fmtHours(rev.totalHours)}</td>
                    <td className="py-2 px-3 font-mono text-right font-bold">
                      {fmtCurrency(rev.grandTotal, rev.currency?.symbol || "$")}
                    </td>
                    <td className="py-2 px-3 text-right">
                      <button
                        onClick={() => handleRestore(rev)}
                        data-testid={`restore-revision-${rev.revisionNumber}`}
                        className="bg-black text-white hover:bg-[#002FA7] rounded-none px-3 py-1.5 font-bold transition-colors text-xs"
                      >
                        Restore
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ProjectsTab({ onLoaded }) {
  const { t } = useTranslation();
  const { savedList, loadEstimate, deleteEstimate, duplicateFromSaved, fetchList, loading } = useEstimate();
  const [search, setSearch] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [historyId, setHistoryId] = useState(null);

  const allTags = useMemo(() => {
    const set = new Set();
    for (const p of savedList) (p.tags || []).forEach((t) => set.add(t));
    return Array.from(set).sort();
  }, [savedList]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return savedList.filter((p) => {
      const matchesQ = !q || (p.projectName || "").toLowerCase().includes(q);
      const matchesTag = !tagFilter || (p.tags || []).includes(tagFilter);
      return matchesQ && matchesTag;
    });
  }, [savedList, search, tagFilter]);

  const handleLoad = async (id) => {
    try {
      await loadEstimate(id);
      toast.success("Estimate loaded");
      if (onLoaded) onLoaded();
    } catch {
      toast.error("Failed to load estimate");
    }
  };
  const handleDuplicate = async (id) => {
    try {
      await duplicateFromSaved(id);
      toast.success("Estimate duplicated", { description: "Edit and Save to persist as a new record." });
      if (onLoaded) onLoaded();
    } catch {
      toast.error("Failed to duplicate");
    }
  };
  const handleShare = async (id) => {
    const url = `${window.location.origin}/share/${id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied", { description: url });
    } catch {
      window.prompt("Copy this read-only share link:", url);
    }
  };
  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete estimate "${name}"? This cannot be undone.`)) return;
    try {
      await deleteEstimate(id);
      toast.success("Estimate deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };
  const symbolFor = (p) => p.currency?.symbol || "$";

  return (
    <section className="space-y-4" data-testid="projects-tab">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
            {t("projects.section")}
          </p>
          <h2 className="font-heading text-2xl tracking-tight font-bold text-black flex items-center gap-2">
            {t("projects.title")}
            <HelpIconButton anchor="ch5" testId="help-icon-projects" />
          </h2>
          <p className="text-sm text-gray-600 mt-1 max-w-2xl">
            {t("projects.description")}
          </p>
        </div>
        <button
          data-testid="refresh-projects-btn"
          onClick={fetchList}
          className="bg-white border border-gray-300 text-black hover:bg-gray-100 rounded-none px-4 py-2 font-bold transition-colors text-sm inline-flex items-center gap-2"
        >
          <RefreshCw size={14} strokeWidth={2} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-300 p-4 flex flex-wrap items-center gap-3" data-testid="projects-filters">
        <div className="inline-flex items-center gap-2 border border-gray-300 px-2 py-1.5 bg-white">
          <Search size={14} strokeWidth={2} className="text-gray-400" />
          <input
            data-testid="projects-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by project name…"
            className="font-mono text-sm focus:outline-none w-64"
          />
        </div>
        <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500 ml-2">
          Tag
        </span>
        <button
          onClick={() => setTagFilter("")}
          data-testid="tag-filter-all"
          className={
            "rounded-none px-3 py-1 text-xs font-bold transition-colors " +
            (tagFilter === "" ? "bg-black text-white" : "bg-white border border-gray-300 text-black hover:bg-gray-100")
          }
        >
          ALL
        </button>
        {allTags.map((t) => (
          <button
            key={t}
            data-testid={`tag-filter-${t}`}
            onClick={() => setTagFilter(t)}
            className={
              "rounded-none px-3 py-1 text-xs font-bold transition-colors uppercase tracking-wider " +
              (tagFilter === t ? "bg-black text-white" : "bg-white border border-gray-300 text-black hover:bg-gray-100")
            }
          >
            {t}
          </button>
        ))}
        <span className="ml-auto font-mono text-xs text-gray-500" data-testid="projects-count">
          {filtered.length} of {savedList.length}
        </span>
      </div>

      <div className="bg-white border border-gray-300 overflow-x-auto">
        <table className="w-full text-sm text-left border-collapse" data-testid="projects-table">
          <thead>
            <tr className="bg-gray-100 text-black font-bold uppercase tracking-wider text-[10px] border-y border-gray-300">
              <th className="py-3 px-3">Project Name</th>
              <th className="py-3 px-3">Tags</th>
              <th className="py-3 px-3 text-right">Total Hours</th>
              <th className="py-3 px-3 text-right">Grand Total</th>
              <th className="py-3 px-3 text-right">Modules</th>
              <th className="py-3 px-3">Currency</th>
              <th className="py-3 px-3 text-right">Rev</th>
              <th className="py-3 px-3">Updated</th>
              <th className="py-3 px-3 w-80"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={9} className="py-12 text-center text-gray-400 text-sm">
                  {savedList.length === 0
                    ? "No saved estimates yet. Enter a project name and click Save to archive your work."
                    : "No estimates match the current filters."}
                </td>
              </tr>
            ) : (
              filtered.map((p) => (
                <tr key={p.id} className="border-b border-gray-200 hover:bg-[#F9FAFB]" data-testid={`project-row-${p.id}`}>
                  <td className="py-3 px-3 font-bold text-black">{p.projectName || "Untitled"}</td>
                  <td className="py-3 px-3">
                    <div className="flex flex-wrap gap-1">
                      {(p.tags || []).map((t) => (
                        <span key={t} className="bg-gray-100 border border-gray-300 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5">
                          {t}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="py-3 px-3 font-mono text-right text-gray-700">{fmtHours(p.totals?.totalHours)}</td>
                  <td className="py-3 px-3 font-mono text-right text-black font-bold">
                    {fmtCurrency(p.totals?.grandTotal ?? p.totals?.totalCost, symbolFor(p))}
                  </td>
                  <td className="py-3 px-3 font-mono text-right text-gray-700">{p.totals?.totalDms ?? 0}</td>
                  <td className="py-3 px-3 font-mono text-xs text-gray-700">{p.currency?.code || "USD"}</td>
                  <td className="py-3 px-3 font-mono text-right text-gray-700">{p.revisionCount ?? 0}</td>
                  <td className="py-3 px-3 font-mono text-xs text-gray-500">
                    {p.updatedAt ? new Date(p.updatedAt).toLocaleString() : "—"}
                  </td>
                  <td className="py-2 px-3 text-right">
                    <div className="inline-flex items-center gap-1 flex-wrap justify-end">
                      <button onClick={() => handleLoad(p.id)} data-testid={`load-project-${p.id}`}
                        className="bg-black text-white hover:bg-[#002FA7] rounded-none px-2.5 py-1.5 font-bold transition-colors text-xs inline-flex items-center gap-1">
                        <FolderOpen size={12} strokeWidth={2} /> Load
                      </button>
                      <button onClick={() => handleDuplicate(p.id)} data-testid={`duplicate-project-${p.id}`}
                        className="bg-white border border-gray-300 text-black hover:bg-gray-100 rounded-none px-2.5 py-1.5 font-bold transition-colors text-xs inline-flex items-center gap-1">
                        <Copy size={12} strokeWidth={2} /> Copy
                      </button>
                      <button onClick={() => setHistoryId(p.id)} data-testid={`history-project-${p.id}`}
                        className="bg-white border border-gray-300 text-black hover:bg-gray-100 rounded-none px-2.5 py-1.5 font-bold transition-colors text-xs inline-flex items-center gap-1">
                        <History size={12} strokeWidth={2} /> History
                      </button>
                      <button onClick={() => handleShare(p.id)} data-testid={`share-project-${p.id}`}
                        className="bg-white border border-gray-300 text-black hover:bg-gray-100 rounded-none px-2.5 py-1.5 font-bold transition-colors text-xs inline-flex items-center gap-1">
                        <Share2 size={12} strokeWidth={2} /> Share
                      </button>
                      <button onClick={() => handleDelete(p.id, p.projectName)} data-testid={`delete-project-${p.id}`}
                        className="bg-white border border-gray-300 text-[#FF2400] hover:bg-red-50 rounded-none px-2.5 py-1.5 font-bold transition-colors text-xs inline-flex items-center gap-1">
                        <Trash2 size={12} strokeWidth={2} /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {historyId && <HistoryModal projectId={historyId} onClose={() => setHistoryId(null)} />}
    </section>
  );
}
